// Centralized content data for VeriData Global website.
// All marketing copy is derived from the official content brief.

export const COMPANY = {
  name: "VeriData Global",
  tagline: "Trusted | Timely | Thorough",
  headline: "Global Research Execution, Delivered with Confidence",
  email: "partnership@veridataglobal.com",
  website: "www.veridataglobal.com",
  logoUrl:
    "https://customer-assets.emergentagent.com/job_c-suite-hub-1/artifacts/awjqrjjk_image.png",
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Industries", to: "/industries" },
  { label: "Case Studies", to: "/case-studies" },
  { label: "Why Us", to: "/why-us" },
  { label: "Contact", to: "/contact" },
];

export const CORE_SERVICES = [
  "Survey Programming",
  "Quantitative Data Collection",
  "Qualitative Data Collection",
  "CATI Services",
  "Online Panels",
  "Data Processing & Analytics",
  "Research Reporting",
  "Multi-Country Fieldwork",
];

export const SERVICES = [
  {
    slug: "survey-programming",
    icon: "Code2",
    title: "Survey Programming",
    short:
      "Transform complex questionnaires into seamless respondent experiences across platforms and devices.",
    long: "A successful research project begins with a well-programmed survey. VeriData Global develops robust survey solutions designed to improve respondent experience, maximize completion rates, and ensure accurate data collection. Whether your study involves simple questionnaires or highly complex methodologies, our programming specialists deliver reliable survey experiences across devices, languages, and markets.",
    capabilities: [
      "Complex skip logic",
      "Multi-language surveys",
      "Mobile optimization",
      "Conjoint studies",
      "MaxDiff studies",
      "Longitudinal research",
    ],
    bullets: [
      "Custom Survey Development",
      "Advanced Logic Programming",
      "Multi-Language Deployment",
      "Mobile Optimization",
      "Quality Assurance Testing",
    ],
    cta: "Ready to launch your next study with confidence?",
  },
  {
    slug: "quantitative-data-collection",
    icon: "BarChart3",
    title: "Quantitative Data Collection",
    short:
      "Verified respondents and structured recruitment to achieve representative samples across global markets.",
    long: "Reliable insights begin with reliable respondents. VeriData Global helps organizations collect accurate, representative data through structured recruitment methodologies and rigorous quality controls. From consumer studies to executive-level research, we provide scalable solutions designed to meet diverse research objectives.",
    capabilities: [
      "Online surveys",
      "Phone-to-web recruitment",
      "Panel recruitment",
      "Multi-country studies",
      "Hard-to-reach audiences",
    ],
    bullets: [
      "Consumer Research",
      "B2B Research",
      "Healthcare Research",
      "Multi-Country Studies",
    ],
    cta: "Let’s discuss your target audience and research objectives.",
  },
  {
    slug: "qualitative-research",
    icon: "Users",
    title: "Qualitative Research",
    short:
      "Capture deeper insights through expertly managed interviews, focus groups, and ethnographic studies.",
    long: "Numbers tell you what is happening. Qualitative research helps you understand why. VeriData Global supports organizations with expertly managed qualitative research programs that uncover motivations, perceptions, attitudes, and decision-making behaviors.",
    capabilities: [
      "In-depth interviews (IDIs)",
      "Focus groups",
      "Online communities",
      "Ethnographic research",
      "Diary studies",
    ],
    bullets: [
      "Executive and B2B interviews",
      "Healthcare qualitative studies",
      "Customer experience research",
      "Product & concept development",
    ],
    cta: "Ready to uncover deeper insights?",
  },
  {
    slug: "cati-services",
    icon: "Phone",
    title: "CATI Services",
    short:
      "Professionally managed telephone interviewing for executive, healthcare, and hard-to-reach audiences.",
    long: "Certain audiences require a more personal approach than online surveys alone. VeriData Global provides CATI services designed to engage executives, healthcare professionals, and specialized respondent groups where online methods alone may not be sufficient.",
    capabilities: [
      "Executive research",
      "Healthcare studies",
      "Market expansion research",
      "Customer experience studies",
      "Hard-to-reach populations",
    ],
    bullets: [
      "Trained interviewers",
      "Multi-lingual capability",
      "Live quality monitoring",
      "Centralized supervision",
    ],
    cta: "Need access to difficult-to-reach respondents?",
  },
  {
    slug: "online-panels",
    icon: "Network",
    title: "Online Research Panels",
    short:
      "Carefully managed panels across consumer, B2B, and healthcare audiences for rapid, high-quality recruitment.",
    long: "Finding the right respondents is one of the most critical factors in research success. VeriData Global provides access to carefully managed online research panels across consumer, B2B, and healthcare audiences. Our panel infrastructure enables rapid recruitment while maintaining high standards for respondent quality and engagement.",
    capabilities: [
      "Quality-focused recruitment",
      "Diverse audience access",
      "Global coverage",
      "Flexible recruitment",
    ],
    bullets: [
      "Consumer audiences",
      "Business professionals",
      "Healthcare audiences",
      "Niche & specialized segments",
    ],
    cta: "Need qualified respondents for your next project?",
  },
  {
    slug: "data-processing-analytics",
    icon: "Database",
    title: "Data Processing & Analytics",
    short:
      "Validate, clean, code, and tabulate research data into analysis-ready deliverables.",
    long: "Transform research data into actionable intelligence. VeriData Global provides data processing services designed to convert raw research outputs into clean, structured, and analysis-ready datasets. Services include data cleaning, validation, coding & categorization, tabulation, and formatting.",
    capabilities: [
      "SPSS files",
      "Excel datasets",
      "Cross-tabulations",
      "Open-end coding",
      "Quality documentation",
    ],
    bullets: [
      "Data Cleaning & Validation",
      "Coding & Categorization",
      "Tabulation & Cross-tabs",
      "Formatting & Delivery",
    ],
    cta: "Need analysis-ready datasets?",
  },
  {
    slug: "research-reporting",
    icon: "PresentationChart",
    title: "Research Reporting",
    short:
      "Executive-ready presentations, dashboards, and analytical summaries that drive confident decisions.",
    long: "Turning research findings into executive decisions. Our reporting team delivers executive-ready presentations, dashboards, and analytical summaries designed for stakeholders at every level — built to help leaders understand findings quickly, confidently, and effectively.",
    capabilities: [
      "Executive presentations",
      "Strategic insight reports",
      "Interactive dashboards",
      "Analytical summaries",
    ],
    bullets: [
      "Stakeholder-ready storytelling",
      "Visual data design",
      "Insight-driven structure",
      "Recommendation frameworks",
    ],
    cta: "Transform findings into meaningful action.",
  },
  {
    slug: "global-fieldwork",
    icon: "Globe",
    title: "Multi-Country Fieldwork",
    short:
      "Single-partner global execution with centralized PM, localized expertise, and consistent quality.",
    long: "Conducting research across multiple countries presents unique challenges — from recruitment and language adaptation to regulatory compliance and project coordination. VeriData Global simplifies multi-country research by providing centralized project management, localized expertise, and consistent quality standards across every market.",
    capabilities: [
      "Centralized project management",
      "Local market expertise",
      "Consistent quality standards",
      "Online, CATI, qualitative, hybrid",
    ],
    bullets: [
      "Single point of contact",
      "Unified reporting & governance",
      "Questionnaire localization",
      "Local respondent recruitment",
    ],
    cta: "Planning a multi-country research project?",
  },
];

export const INDUSTRIES = [
  {
    icon: "HeartPulse",
    name: "Healthcare & Life Sciences",
    blurb:
      "Recruit physicians, allied professionals, healthcare decision-makers, patients, and caregivers through specialized, compliant methodologies.",
    audiences: [
      "Primary care physicians & specialists",
      "Nurses, pharmacists, technicians",
      "Hospital administrators & executives",
      "Condition-specific patients & caregivers",
    ],
  },
  {
    icon: "Briefcase",
    name: "B2B & Professional Services",
    blurb:
      "Access executives, decision-makers, and industry specialists across diverse sectors and global markets.",
    audiences: [
      "C-suite & senior leadership",
      "Vice Presidents & Directors",
      "Functional decision-makers",
      "Industry specialists",
    ],
  },
  {
    icon: "ShoppingBag",
    name: "Consumer Research",
    blurb:
      "Understand consumer behaviors, preferences, and purchase drivers through scalable research solutions.",
    audiences: [
      "General population",
      "Category users & product owners",
      "Affluent consumers",
      "Special-interest segments",
    ],
  },
  {
    icon: "Cpu",
    name: "Technology & Innovation",
    blurb:
      "Support product development, customer experience, and market expansion with reliable research execution.",
    audiences: [
      "Enterprise IT buyers",
      "Developers & engineers",
      "Product end-users",
      "Innovation & R&D leaders",
    ],
  },
  {
    icon: "Landmark",
    name: "Financial Services",
    blurb:
      "Deliver insights across banking, insurance, wealth management, and fintech — with rigour and discretion.",
    audiences: [
      "Retail & affluent banking customers",
      "Wealth & investment clients",
      "Insurance decision-makers",
      "Fintech adopters",
    ],
  },
];

export const REGIONS = [
  {
    region: "Asia-Pacific",
    countries: [
      "India",
      "Singapore",
      "Malaysia",
      "Thailand",
      "Indonesia",
      "Vietnam",
      "Japan",
      "Australia",
    ],
  },
  {
    region: "Middle East",
    countries: ["UAE", "Saudi Arabia", "Qatar", "Kuwait", "Bahrain", "Oman"],
  },
  {
    region: "North America",
    countries: ["United States", "Canada"],
  },
  {
    region: "Europe",
    countries: ["United Kingdom", "Germany", "France", "Netherlands", "Spain"],
  },
];

export const VALUE_PROPS = [
  {
    icon: "ShieldCheck",
    title: "Quality Without Compromise",
    body: "Every project follows rigorous quality control standards designed to ensure reliable data and confident decision-making.",
  },
  {
    icon: "Globe2",
    title: "Global Reach, Local Expertise",
    body: "Execute studies across multiple markets through a single partner while benefiting from localized knowledge, language expertise, and cultural understanding.",
  },
  {
    icon: "Workflow",
    title: "Operational Simplicity",
    body: "Reduce vendor management complexity with one team, one process, and one point of accountability throughout the project lifecycle.",
  },
];

export const QUALITY_COMMITMENT = [
  "Respondent verification",
  "Multi-layer quality controls",
  "Fraud prevention protocols",
  "Secure data handling",
  "International compliance standards",
  "Transparent project management",
];

export const STATS = [
  { value: "20+", label: "Languages supported" },
  { value: "25+", label: "Countries covered" },
  { value: "4", label: "Continents reached" },
  { value: "1", label: "Single partner. Full lifecycle." },
];

export const CASE_STUDIES = [
  {
    title: "Specialist Physician Feedback Across Markets",
    industry: "Healthcare & Life Sciences",
    challenge:
      "A global healthcare client required structured feedback from specialist physicians across multiple regions within a short timeline.",
    approach:
      "VeriData Global activated specialist recruitment networks and deployed multi-mode fieldwork to access the required cohort with verification at every step.",
    outcomes: [
      "Successful recruitment of niche specialists",
      "On-time delivery against tight study window",
      "High-quality, validated dataset",
    ],
    metric: "100%",
    metricLabel: "On-time delivery",
  },
  {
    title: "B2B Executive Research at Scale",
    industry: "B2B & Professional Services",
    challenge:
      "A global consulting firm needed perspectives from senior decision-makers across complex enterprise environments.",
    approach:
      "Targeted recruitment combined with CATI and qualified online panels produced a balanced executive sample with strong participation.",
    outcomes: [
      "Access to verified C-suite & VP audiences",
      "Strong participation rates",
      "Actionable insights for strategy refresh",
    ],
    metric: "C-Suite",
    metricLabel: "Verified executive sample",
  },
  {
    title: "Multi-Country Consumer Study",
    industry: "Consumer Research",
    challenge:
      "A global brand required consistent methodology and comparability across multiple markets simultaneously.",
    approach:
      "Centralized program management, localized questionnaire adaptation, and unified quality standards ensured a coherent multi-market execution.",
    outcomes: [
      "Consistent coverage across markets",
      "Reliable comparative insights",
      "Successful program delivery",
    ],
    metric: "Multi-Market",
    metricLabel: "Comparable insights",
  },
];

export const PROJECT_TYPES = [
  "Survey Programming",
  "Quantitative Data Collection",
  "Qualitative Research",
  "CATI Services",
  "Online Panels",
  "Data Processing & Analytics",
  "Research Reporting",
  "Multi-Country Fieldwork",
  "Other",
];
